import React, { useState, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import ListLoader from "../../common-components/ListLoader";
import PrimaryButton from "../../common-components/Buttons/PrimaryButton";
import AlertButton from "../../common-components/Buttons/AlertButton";
import { ToastContainer } from "react-toastify";
import useGetCampaigns from "./hooks/useGetCampaigns";
import useDeleteCampaign from "./hooks/useDeleteCampaign";
import CreateCampaignModal from "./CreateCampaignModal";
import CampaignPreviewModal from "./CampaignPreviewModal";
import CampaignFilters from "./CampaignFilters";
import { FaEye, FaEdit, FaTrash } from "react-icons/fa";
import { formatIST } from "./utils/dateUtils";

const CampaignManagement = () => {
  const { t } = useTranslation();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [isEditable, setIsEditable] = useState(false);
  const [currentCampaign, setCurrentCampaign] = useState({});
  const [previewCampaign, setPreviewCampaign] = useState(null);
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");

  const { campaigns, isCampaignsLoading, getCampaigns } = useGetCampaigns({
    category,
    status,
  });

  const { onDelete, isCampaignDeleting } = useDeleteCampaign({
    getAllCampaigns: getCampaigns,
  });

  // Filter campaigns by search
  const filteredCampaigns = useMemo(() => {
    return campaigns.filter((c) =>
      c.title?.toLowerCase().includes(search.toLowerCase())
    );
  }, [campaigns, search]);

  const handleAddCampaign = () => {
    setCurrentCampaign({});
    setIsEditable(false);
    setIsCreateModalOpen(true);
  };

  const handleEdit = (campaign) => {
    setCurrentCampaign(campaign);
    setIsEditable(true);
    setIsCreateModalOpen(true);
  };

  const handlePreview = (campaign) => {
    setPreviewCampaign(campaign);
    setIsPreviewModalOpen(true);
  };

  const handleDelete = async (campaign) => {
    if (window.confirm(`Are you sure you want to delete "${campaign.title}"?`)) {
      await onDelete(campaign.id);
    }
  };

  // Campaign list component
  const CampaignList = ({ showActions = true }) => {
    if (isCampaignsLoading) {
      return <ListLoader />;
    }

    if (filteredCampaigns.length === 0) {
      return (
        <div className="p-2 flex justify-center text-red-500">
          {t("No campaigns found")} !
        </div>
      );
    }

    return (
      <>
        {filteredCampaigns.map((campaign) => {
          return (
            <div
              key={campaign.id}
              className={`grid ${showActions ? 'grid-cols-7' : 'grid-cols-6'} border-b items-center last:border-b-0 p-2`}
            >
              <div>{campaign.title}</div>
              <div className="capitalize">{campaign.category || "--"}</div>
              <div>
                {campaign.status === "active"
                  ? t("Active")
                  : campaign.status === "inactive"
                  ? t("Inactive")
                  : campaign.status === "draft"
                  ? t("Draft")
                  : "--"}
              </div>
              <div>{formatIST(campaign.start_date) || "--"}</div>
              <div>{formatIST(campaign.end_date) || "--"}</div>
              {showActions && (
                <div className="flex items-center justify-between gap-2">
                  <button onClick={() => handlePreview(campaign)}>
                    <FaEye color="#4C6AF7" />
                  </button>
                  <button onClick={() => handleEdit(campaign)}>
                    <FaEdit color="#4C6AF7" />
                  </button>
                  <AlertButton
                    buttonName={
                      isCampaignDeleting ? t("Deleting") : t("Delete")
                    }
                    onClick={() => handleDelete(campaign)}
                    disabled={isCampaignDeleting}
                  />
                </div>
              )}
            </div>
          );
        })}
      </>
    );
  };

  // Table header component
  const TableHeader = ({ showActions = true }) => (
    <div className={`grid px-2 py-2 text-base border-b uppercase items-center ${showActions ? 'grid-cols-7' : 'grid-cols-6'} bg-[#F9FAFB] text-[#6B7280]`}>
      <div>{t("Title")}</div>
      <div>{t("Category")}</div>
      <div>{t("Status")}</div>
      <div>{t("Start Date")}</div>
      <div>{t("End Date")}</div>
      {showActions && <div></div>}
    </div>
  );

  return (
    <div className="m-5 text-sm text-primary">
      <ToastContainer />
      <div className="my-5">
        <div className="flex items-center justify-between">
          <div className="text-base font-medium">{t("Campaign Management")}</div>
          <div className="gap-5 flex items-center">
            <PrimaryButton
              buttonName={t("Add Campaign")}
              onClick={handleAddCampaign}
            />
          </div>
        </div>

        <div className="mt-5 overflow-visible">
          <CampaignFilters
            search={search}
            setSearch={setSearch}
            category={category}
            setCategory={setCategory}
            status={status}
            setStatus={setStatus}
            onAddCampaign={handleAddCampaign}
          />
          <div className="border">
            <TableHeader />
            <CampaignList />
          </div>
        </div>
      </div>

      <CreateCampaignModal
        isOpen={isCreateModalOpen}
        setIsOpen={setIsCreateModalOpen}
        getAllCampaigns={getCampaigns}
        isEditable={isEditable}
        currentCampaign={currentCampaign}
        setCurrentCampaign={setCurrentCampaign}
        setIsEditable={setIsEditable}
      />

      <CampaignPreviewModal
        isOpen={isPreviewModalOpen}
        onClose={() => setIsPreviewModalOpen(false)}
        campaign={previewCampaign}
      />
    </div>
  );
};

export default CampaignManagement;

